import { useRef } from "react";
import { useDispatch } from "react-redux";
import { ModalPortal } from "../../SharedModule/components/ModalPortal";
import { ModalDeleteTimeEntry } from "../components/ModalDeleteTimeEntry";
import { addToast } from "../../SharedModule/redux/reducers/ui.reducer";
import { TimesheetsService } from "../services/timesheets";
import { isImportedFromBambooNotHolidayChecker } from "../utils/timesUtils";

type DeletableTimeEntry = {
  timeEntryId: number;
};

// Shared by QueryResultsPage and ApproveTimeByUserPage: the "delete time
// entry" modal and its confirm action are wired up identically in both.
// TimesPage is excluded - its deleteEntryModal has extra sheet-view logic.
export const useDeleteTimeEntryModal = <T extends DeletableTimeEntry>(
  activeEntry: T,
  isSuperTimeAdmin: boolean,
  onDeleteStart: () => void,
  refreshPage: () => void
) => {
  const dispatch = useDispatch();

  const modalDeleteRef = useRef<undefined>(undefined);
  const showDeleteModal = () => {
    (modalDeleteRef.current as any).show(true);
  };
  const closeDeleteModal = () => {
    (modalDeleteRef.current as any).show(false);
  };

  const deleteEntryModal = async () => {
    closeDeleteModal();
    onDeleteStart();

    // ASUITE11-2736: can delete Paid Time Off(taskTypeId:32) or Unpaid Time Off(taskTypeId:42) from bamboo for others
    let isImportedFromBambooNotHoliday: boolean =
      isImportedFromBambooNotHolidayChecker(activeEntry);
    let body = {
      timeEntriesIds: [activeEntry.timeEntryId],
      action: {
        id: 10,
        nonBillableReasonId: null,
        entryDate: null,
        entityId: null,
        categoryId: null,
      },
    };
    if (isImportedFromBambooNotHoliday && isSuperTimeAdmin) {
      await TimesheetsService.updateTimeEntries(body)
        .then((response) => {
          dispatch(
            addToast({ mode: "success", message: response.data.message })
          );
          refreshPage();
        })
        .catch((error) => {
          dispatch(addToast({ mode: "error", message: error }));
        });
    } else {
      await TimesheetsService.deleteTimesEntry(activeEntry.timeEntryId)
        .then((response) => {
          dispatch(
            addToast({ mode: "success", message: response.data.message })
          );
          refreshPage();
        })
        .catch((error) => {
          dispatch(addToast({ mode: "error", message: error }));
        });
    }
  };

  const deleteModalPortal = (
    <ModalPortal ref={modalDeleteRef}>
      <ModalDeleteTimeEntry
        btnCancel={closeDeleteModal}
        btnAction={deleteEntryModal}
      />
    </ModalPortal>
  );

  return { showDeleteModal, deleteEntryModal, deleteModalPortal };
};
